/**
 * Two-factor authentication (TOTP) constants.
 *
 * Microsoft Authenticator is a standard RFC 6238 TOTP app, so enrollment goes
 * through Supabase's native MFA API — there is no Microsoft SDK involved and the
 * same factor works with Google Authenticator, Authy or 1Password.
 */

/**
 * When true, users without a verified authenticator are forced through
 * /two-factor before they can reach any protected route.
 *
 * This is a hard lock: flipping it on blocks every existing user until they
 * enroll. Keep it as a flag so the rollout can be reversed without redeploying
 * the gate logic.
 *
 * Currently false — mandatory enrollment is on hold. Note this only pauses the
 * *forced* enrollment; users who already enrolled are still challenged for their
 * code on sign-in (see needsChallenge in ProtectedRoute), and enrollment from
 * Settings stays available. Flip back to true to resume the rollout.
 */
export const MFA_REQUIRED = false;

/**
 * Skips the /two-factor gate — no code prompt on sign-in, no forced enrollment.
 *
 * Local dev only, deliberately double-guarded: `import.meta.env.DEV` is false in
 * any `npm run build` (which is what both deploy workflows run), and the
 * hostname check keeps it off even in a `build:dev` bundle served from staging.
 * Staging and production keep two-factor exactly as it is today.
 *
 * Enrolling from Settings still works while this is on — the session just stays
 * at aal1 instead of being stepped up.
 */
export const MFA_BYPASS =
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);

/** Internal label for the factor, returned by listFactors(). Never shown on the phone. */
export const MFA_FRIENDLY_NAME = 'Sellaris';

/**
 * The name the user actually sees in Microsoft Authenticator. Supabase defaults
 * this to the site URL, which renders as a bare "bigesales.com" entry.
 */
export const MFA_ISSUER = 'Sellaris';

/** Where users are sent to enroll or to answer a challenge. */
export const MFA_ROUTE = '/two-factor';

/**
 * How long a successful TOTP verification trusts *this browser* before the code
 * is required again. Signing in from the same browser within this window skips
 * the challenge; a new device, a cleared browser, or an expired window does not.
 */
export const MFA_REMEMBER_HOURS = 48;

const rememberKey = (userId: string) => `bigE_mfa_trusted_${userId}`;

/**
 * Records that this browser just answered a TOTP challenge, so the same user is
 * not re-prompted for MFA_REMEMBER_HOURS.
 *
 * The trust is device-bound — it lives only in this browser's localStorage — so
 * a stolen password used on any other device still hits the challenge. Keyed by
 * user id so a shared browser never lets one account skip on another's behalf.
 */
export const rememberMfaDevice = (userId: string) => {
  if (typeof window === 'undefined') return;
  const expiresAt = Date.now() + MFA_REMEMBER_HOURS * 60 * 60 * 1000;
  try {
    window.localStorage.setItem(rememberKey(userId), String(expiresAt));
  } catch {
    // Private mode / storage full — fail closed: the user is simply challenged.
  }
};

/** True when this browser answered a TOTP challenge within the trust window. */
export const isMfaDeviceRemembered = (userId: string) => {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.localStorage.getItem(rememberKey(userId));
    if (!raw) return false;
    const expiresAt = Number(raw);
    if (!Number.isFinite(expiresAt) || Date.now() >= expiresAt) {
      window.localStorage.removeItem(rememberKey(userId));
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

/** Clears this browser's trust, forcing the code on the next sign-in. */
export const forgetMfaDevice = (userId: string) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(rememberKey(userId));
  } catch {
    // ignore
  }
};

/** Supabase authenticator assurance level. */
export type AalLevel = 'aal1' | 'aal2' | null;

/**
 * True when the session is authenticated but still owes a TOTP code — a verified
 * factor exists (nextLevel is aal2), yet the current token is only aal1.
 */
export const needsChallenge = (currentLevel: AalLevel, nextLevel: AalLevel) =>
  currentLevel === 'aal1' && nextLevel === 'aal2';

/**
 * True when the user has no verified factor at all. Supabase reports nextLevel as
 * 'aal1' precisely when there is nothing to step up to.
 */
export const needsEnrollment = (nextLevel: AalLevel) => nextLevel === 'aal1';
